import { z } from "zod";

const semverPattern =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*)(?:\.(?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*))*))?(?:\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;

export const idSchema = z.uuid();
export const timestampSchema = z.iso.datetime({ offset: true });
export const semverSchema = z.string().trim().regex(semverPattern, "Use a valid SemVer version");
export const slugSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase words separated by hyphens");
export const channelSchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, "Use a lowercase channel identifier");
export const platformSchema = z.enum(["any", "windows", "macos", "linux"]);
export const architectureSchema = z.enum(["any", "universal", "x64", "arm64", "x86"]);
export const httpsUrlSchema = z
  .url()
  .refine((value) => new URL(value).protocol === "https:", "Use an HTTPS URL");

export const applicationSchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(120),
  slug: slugSchema,
  description: z.string().max(1_000).nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const createApplicationSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  description: z.string().trim().max(1_000).nullable().optional().default(null),
});

export const updateApplicationSchema = createApplicationSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, "Provide at least one field");

export const releaseSchema = z.object({
  id: idSchema,
  applicationId: idSchema,
  version: semverSchema,
  channel: channelSchema,
  platform: platformSchema,
  architecture: architectureSchema,
  installerUrl: httpsUrlSchema,
  releaseNotes: z.string().max(10_000).nullable(),
  mandatory: z.boolean(),
  publishedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const createReleaseSchema = z.object({
  version: semverSchema,
  channel: channelSchema.optional().default("stable"),
  platform: platformSchema.optional().default("any"),
  architecture: architectureSchema.optional().default("any"),
  installerUrl: httpsUrlSchema,
  releaseNotes: z.string().trim().max(10_000).nullable().optional().default(null),
  mandatory: z.boolean().optional().default(false),
  publishedAt: timestampSchema.nullable().optional().default(null),
});

export const updateReleaseSchema = createReleaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, "Provide at least one field");

export const applicationsResponseSchema = z.object({ data: z.array(applicationSchema) });
export const applicationResponseSchema = z.object({ data: applicationSchema });
export const releasesResponseSchema = z.object({ data: z.array(releaseSchema) });
export const releaseResponseSchema = z.object({ data: releaseSchema });
export const deleteResponseSchema = z.object({ deleted: z.literal(true) });

export const updateCheckQuerySchema = z.object({
  current_version: semverSchema,
  channel: channelSchema.optional().default("stable"),
  platform: platformSchema.optional().default("any"),
  architecture: architectureSchema.optional().default("any"),
});

export const updateCheckResponseSchema = z.object({
  updateAvailable: z.boolean(),
  currentVersion: semverSchema,
  application: applicationSchema.pick({ id: true, name: true, slug: true }),
  release: releaseSchema.omit({ applicationId: true }).nullable(),
});

export const authSessionSchema = z.object({
  authenticated: z.literal(true),
  user: z.object({
    id: z.string().min(1),
    email: z.email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
  }),
  csrfToken: z.string().min(32),
});

export const logoutResponseSchema = z.object({ logoutUrl: z.url() });

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string().optional(),
    details: z.unknown().optional(),
  }),
});

export type Application = z.infer<typeof applicationSchema>;
export type CreateApplication = z.infer<typeof createApplicationSchema>;
export type UpdateApplication = z.infer<typeof updateApplicationSchema>;
export type Release = z.infer<typeof releaseSchema>;
export type CreateRelease = z.infer<typeof createReleaseSchema>;
export type UpdateRelease = z.infer<typeof updateReleaseSchema>;
export type UpdateCheckQuery = z.infer<typeof updateCheckQuerySchema>;
export type UpdateCheckResponse = z.infer<typeof updateCheckResponseSchema>;
export type AuthSession = z.infer<typeof authSessionSchema>;
export type ApiError = z.infer<typeof apiErrorSchema>;
export type Platform = z.infer<typeof platformSchema>;
export type Architecture = z.infer<typeof architectureSchema>;
